/**
 * DayExport.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { PdfExport } from './PdfExport.js'
import { HtmlExport } from './HtmlExport.js'
import { createPdfDocument, applyPdfMetadata, dayPdfMetadata } from './PdfMetadata.js'
import { chainedPlan } from '../core/DayPlanner.js'
import { exposureShown, surfaceIntervalSentences, noFlyValue, formatDuration } from './planUtils.js'

// Export of a day of diving: the series on one document.
//
// Nothing is recomputed here and no plan is built: only the dives the day
// already carries are exported. A dive with no computed plan is listed as such
// rather than silently skipped, because a sheet that quietly drops the third
// dive of the day is worse than one that says it is missing.
export class DayExport {
  // dives     the day, straight from store.state.day.dives
  // settings, environment, gases  the shared inputs of the series
  // translateFn                   i18n.t
  constructor(dives, settings, environment, gases, translateFn) {
    this.dives = Array.isArray(dives) ? dives : []
    this.settings = settings
    this.environment = environment || {}
    this.gases = gases || []
    this.t = translateFn || (key => key)

    if (!Array.isArray(dives)) {
      console.error('DayExport: the day is not a list of dives (%o), an empty day is exported', dives)
    }
  }

  // Dives that carry a computed plan, with their position in the day kept:
  // dive 3 stays dive 3 on the sheet even when dive 2 was not computed.
  computedDives() {
    return this.dives
      .map((dive, index) => ({ dive, index, plan: chainedPlan(dive.results) }))
      .filter(entry => entry.plan)
  }

  // Bottles the exporters print, exactly what the plan screen hands them
  // (Store.getBailoutGases): enabled AND declared as bailout. Passing every
  // enabled mix would list bottles on the sheet that the plan never used.
  bailoutGases() {
    return this.gases.filter(gas => gas.enabled && gas.isBailout)
  }

  // The rebreather configuration of a dive, in the shape the exporters expect.
  // A dive of the day carries its own; an open circuit dive carries none.
  ccrOf(dive) {
    return dive.ccr?.enabled ? dive.ccr : { enabled: false }
  }

  // Totals of the day, read off the last computed dive: the oxygen clock and
  // the surface interval information accumulate along the series.
  totals() {
    const computed = this.computedDives()
    if (computed.length === 0) return null

    const last = computed[computed.length - 1].plan
    const exposure = exposureShown(last)
    const t = (key, params) => this.t(key, params)
    const { desat, danCase, danRule, danSource } = surfaceIntervalSentences(last, t, this.environment)

    return {
      count: computed.length,
      total: this.dives.length,
      cns: exposure.cns,
      otu: exposure.otu,
      cnsWarning: exposure.cnsWarning,
      otuWarning: exposure.otuWarning,
      noFly: noFlyValue(last, t),
      sentences: [desat, danCase, danRule, danSource].filter(Boolean).join(' ')
    }
  }

  // One PDF, one page per computed dive, and a last page with the totals.
  //
  // The condensed fiche is reused as it is: what a diver takes on the boat for
  // a single dive is exactly what he needs for each dive of a series, and it
  // fits one page.
  buildPdf() {
    const computed = this.computedDives()
    if (computed.length === 0) {
      console.error('DayExport.buildPdf: no dive of the day carries a computed plan, nothing to export')
      return null
    }

    // The document is created here, and its metadata describes the series: a
    // sheet handed an existing document writes none, and jsPDF keeps the first
    // XMP packet it is given, so a day built by chaining sheets would otherwise
    // be filed under its first dive.
    const doc = applyPdfMetadata(createPdfDocument(), dayPdfMetadata({
      computed,
      total: this.dives.length,
      settings: this.settings,
      environment: this.environment
    }))

    computed.forEach(({ dive, index }, position) => {
      const exporter = new PdfExport(
        dive.results, this.settings, this.ccrOf(dive), (key, params) => this.t(key, params),
        this.bailoutGases(), this.environment
      )
      if (position > 0) doc.addPage()
      exporter.generateCondensedPdf(doc)
      this.stampDiveHeader(doc, dive, index)
    })

    this.appendTotalsPage(doc)
    return doc
  }

  // Where the dive sits in the day, printed on its page: a sheet of four pages
  // with no numbering is four sheets nobody can put back in order.
  stampDiveHeader(doc, dive, index) {
    const pageWidth = doc.internal.pageSize.getWidth()
    const label = dive.label ? ` - ${dive.label}` : ''
    const interval = index === 0
      ? this.t('day_first_dive')
      : `${this.t('day_surface_interval')} ${formatDuration(dive.surfaceIntervalBefore ?? 0, (key, params) => this.t(key, params))}`

    // Bottom of the page, above the footer of the sheet: the top is already
    // taken by the title and the summary boxes of the condensed fiche, and a
    // line written there lands on top of them.
    const y = doc.internal.pageSize.getHeight() - 20
    doc.setFontSize(9)
    doc.setTextColor(90)
    doc.text(`${this.t('day_dive_number', { number: index + 1, total: this.dives.length })}${label} - ${interval}`,
      pageWidth / 2, y, { align: 'center' })
    doc.setTextColor(0)
  }

  // Last page: what the diver leaves the water with after the whole series.
  appendTotalsPage(doc) {
    const totals = this.totals()
    if (!totals) return

    doc.addPage()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 15
    let y = 25

    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(this.t('day_summary'), margin, y)
    y += 10

    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    const rows = [
      [this.t('day_dives_count'), String(totals.count)],
      [this.t('plan_cns_series'), `${totals.cns}%`],
      [this.t('plan_otu_series'), String(totals.otu)]
    ]
    if (totals.noFly) rows.push([this.t('plan_no_fly'), totals.noFly])

    for (const [label, value] of rows) {
      doc.setTextColor(90)
      doc.text(label, margin, y)
      doc.setTextColor(0)
      doc.text(value, margin + 60, y)
      y += 7
    }

    if (totals.count < totals.total) {
      y += 3
      doc.setFontSize(9)
      doc.setTextColor(150, 80, 0)
      doc.text(this.t('day_summary_partial', { computed: totals.count, total: totals.total }),
        margin, y, { maxWidth: pageWidth - 2 * margin })
      doc.setTextColor(0)
      y += 8
    }

    if (totals.sentences) {
      y += 3
      doc.setFontSize(9)
      doc.setTextColor(90)
      doc.text(totals.sentences, margin, y, { maxWidth: pageWidth - 2 * margin })
      doc.setTextColor(0)
    }
  }

  // One HTML document holding every dive of the day, then the totals.
  //
  // Each dive is rendered by the very exporter the plan screen uses, so a dive
  // of a series and a dive on its own print the same tables. Only the body of
  // each document is kept, and they are wrapped in one page.
  buildHtml() {
    const computed = this.computedDives()
    if (computed.length === 0) {
      console.error('DayExport.buildHtml: no dive of the day carries a computed plan, nothing to export')
      return null
    }

    let diveStyles = ''
    const sections = computed.map(({ dive, index }) => {
      const exporter = new HtmlExport(
        dive.results, this.settings, this.ccrOf(dive), (key, params) => this.t(key, params),
        this.bailoutGases(), this.environment
      )
      const document = exporter.generateHtml()
      if (!diveStyles) diveStyles = DayExport.stylesOf(document)
      const body = DayExport.bodyOf(document)
      const label = dive.label ? ` - ${DayExport.escape(dive.label)}` : ''
      const interval = index === 0
        ? this.t('day_first_dive')
        : `${this.t('day_surface_interval')} ${formatDuration(dive.surfaceIntervalBefore ?? 0, (key, params) => this.t(key, params))}`

      return `
        <section style="page-break-after: always; margin-bottom: 24px;">
          <h2 style="font-size: 15px; color: #005096; border-bottom: 2px solid #005096; padding-bottom: 4px;">
            ${this.t('day_dive_number', { number: index + 1, total: this.dives.length })}${label}
            <span style="font-size: 11px; color: #666; font-weight: normal;"> - ${interval}</span>
          </h2>
          ${body}
        </section>
      `
    })

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.t('day_title')}</title>
  <style>
${diveStyles}
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 16px; background: #fff; color: #222; }
    h1 { font-size: 20px; color: #005096; margin: 0 0 12px 0; }
  </style>
</head>
<body>
  <h1>${this.t('day_title')}</h1>
  ${sections.join('\n')}
  ${this.totalsHtml()}
</body>
</html>`
  }

  totalsHtml() {
    const totals = this.totals()
    if (!totals) return ''

    const cell = (label, value, warn) => `
      <div style="text-align: center; padding: 10px 5px; background: #e6f5ff; border-radius: 6px;">
        <div style="font-size: 16px; font-weight: bold; color: ${warn ? '#856404' : '#005096'};">${value}</div>
        <div style="font-size: 9px; color: #666;">${label}</div>
      </div>`

    return `
      <section>
        <h2 style="font-size: 15px; color: #005096; border-bottom: 2px solid #005096; padding-bottom: 4px;">${this.t('day_summary')}</h2>
        <div style="display: grid; grid-template-columns: repeat(${totals.noFly ? 4 : 3}, 1fr); gap: 8px; margin-top: 8px;">
          ${cell(this.t('day_dives_count'), totals.count, false)}
          ${cell(this.t('plan_cns_series'), `${totals.cns}%`, totals.cnsWarning)}
          ${cell(this.t('plan_otu_series'), totals.otu, totals.otuWarning)}
          ${totals.noFly ? cell(this.t('plan_no_fly'), totals.noFly, false) : ''}
        </div>
        ${totals.count < totals.total
          ? `<p style="font-size: 11px; color: #856404; margin-top: 8px;">${this.t('day_summary_partial', { computed: totals.count, total: totals.total })}</p>`
          : ''}
        ${totals.sentences ? `<p style="font-size: 11px; color: #666; margin-top: 8px;">${totals.sentences}</p>` : ''}
      </section>
    `
  }

  // Styles of a dive sheet, taken once and hoisted into the head of the day
  // document. Without them the sections keep their markup and lose every rule
  // that makes a plan table readable - the sheets print as raw text.
  static stylesOf(html) {
    const match = /<style[^>]*>([\s\S]*?)<\/style>/i.exec(html)
    if (!match) {
      console.error('DayExport.stylesOf: no style block found in the exported dive, the day document is printed unstyled')
      return ''
    }
    return match[1]
  }

  // Body of a full HTML document. Falls back on the whole document when the
  // markers are missing, with a log: a sheet with too much on it can still be
  // read, an empty one cannot.
  static bodyOf(html) {
    const match = /<body[^>]*>([\s\S]*)<\/body>/i.exec(html)
    if (!match) {
      console.error('DayExport.bodyOf: no body found in the exported dive, the whole document is embedded')
      return html
    }
    return match[1]
  }

  static escape(text) {
    return String(text).replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ))
  }

  // Save the day as a PDF file. Returns false when there was nothing to export
  // or when the browser refused the download, both already logged.
  savePdf() {
    let doc
    try {
      doc = this.buildPdf()
    } catch (e) {
      console.error('DayExport.savePdf: building the document failed:', e)
      return false
    }
    if (!doc) return false

    try {
      doc.save(`dive-day-${new Date().toISOString().split('T')[0]}.pdf`)
      return true
    } catch (e) {
      console.error('DayExport.savePdf: saving the document failed:', e)
      return false
    }
  }

  // Save the day as an HTML file, the same way a single plan is exported.
  saveHtml() {
    let html
    try {
      html = this.buildHtml()
    } catch (e) {
      console.error('DayExport.saveHtml: building the document failed:', e)
      return false
    }
    if (!html) return false

    try {
      const blob = new Blob([html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `dive-day-${new Date().toISOString().split('T')[0]}.html`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      return true
    } catch (e) {
      console.error('DayExport.saveHtml: saving the document failed:', e)
      return false
    }
  }
}
